import { hasLocale } from '@/i18n/locale'

import { formats } from './formats'
import { defaultLocale, locales, type Locale } from './locales'

type NumberPreset = keyof typeof formats.number
type DateTimePreset = keyof typeof formats.dateTime

export interface LocaleFormatter {
    number(value: number | bigint, options?: Intl.NumberFormatOptions | NumberPreset): string
    dateTime(value: Date | number, options?: Intl.DateTimeFormatOptions | DateTimePreset): string
}

/** A formatter per locale, built once, each keeping the `Intl` objects it builds by their
 *  options, so a page does not rebuild an `Intl.NumberFormat` for every fare it renders. */
const formatters = new Map<Locale, LocaleFormatter>()

function cached<Options, Format>(build: (options: Options) => Format) {
    const built = new Map<string, Format>()
    return (options: Options): Format => {
        const key = JSON.stringify(options)
        let format = built.get(key)
        if (format === undefined) {
            format = build(options)
            built.set(key, format)
        }
        return format
    }
}

function createLocaleFormatter(locale: Locale): LocaleFormatter {
    const numberFormat = cached(
        (options: Intl.NumberFormatOptions) => new Intl.NumberFormat(locale, options),
    )
    const dateTimeFormat = cached(
        (options: Intl.DateTimeFormatOptions) =>
            new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options }),
    )
    return {
        number: (value, options = {}) => {
            try {
                return numberFormat(
                    typeof options === 'string' ? formats.number[options] : options,
                ).format(value)
            } catch {
                return String(value)
            }
        },
        dateTime: (value, options = {}) => {
            try {
                return dateTimeFormat(
                    typeof options === 'string' ? formats.dateTime[options] : options,
                ).format(value)
            } catch {
                return String(value)
            }
        },
    }
}

/**
 * The app's shared formatter for a locale — the one stack every rendered number, price and
 * date goes through, so a locale's conventions are decided in one place rather than at each
 * call site's `Intl` invocation. A format that throws — an option `Intl` refuses — renders
 * the plain value rather than failing the render.
 *
 * Usable from plain library code, server components and client components alike: it takes
 * the locale as an argument rather than reading React context, so a transform running
 * outside the render tree formats exactly as a component does. Components get the locale
 * from `useLocale()` and hand it here.
 *
 * **`dateTime` is pinned to UTC** (→ invariants.md#ssr-safe-rendered-dates). Every date
 * this app renders is a calendar date the warehouse or the URL already fixed, never an
 * instant to project into the reader's zone, so the caller builds it from explicit Y/M/D
 * parts at UTC (`Date.UTC(...)`) and reads it back in UTC. Leaving the zone implicit would
 * take the *process* zone instead, making SSR and hydration disagree — a React #418.
 */
export function localeFormatter(locale: string): LocaleFormatter {
    // Total over whatever string reaches it: a locale the app does not serve falls back
    // to the default rather than reaching `Intl`, which throws a `RangeError` on a
    // malformed tag. A render path may not fail over a locale it could not parse.
    //
    // Case-folded first because `hasLocale` matches the served list exactly while BCP-47
    // itself is case-insensitive — so the conventional `es-MX` would otherwise fall back
    // to English *while looking served*, formatting Spanish copy with English
    // conventions and reporting nothing.
    const tag = locale.toLowerCase()
    const served: Locale = hasLocale(locales, tag) ? tag : defaultLocale

    let formatter = formatters.get(served)
    if (!formatter) {
        formatter = createLocaleFormatter(served)
        formatters.set(served, formatter)
    }
    return formatter
}
